import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { FileText, Table, Download, FolderOpen, Users, BarChart, Megaphone, Shield, CreditCard } from 'lucide-react';

const iconMap = {
  'Full_Application': FileText,
  'Handover_PM': Users,
  'Handover_Marketing': Megaphone,
  'Handover_Design': BarChart,
  'Handover_Sales': CreditCard,
  'Handover_Deployment': Shield,
  'Project_Plan': Table,
  'Deployment_Guide': Shield,
  'Commercial': CreditCard,
};

const colorMap = {
  'docx': 'from-blue-500 to-indigo-500',
  'xlsx': 'from-emerald-500 to-teal-500',
};

const categoryMap = {
  'Full_Application': 'Documentation',
  'Handover_PM': 'Handover',
  'Handover_Marketing': 'Handover',
  'Handover_Design': 'Handover',
  'Handover_Sales': 'Handover',
  'Handover_Deployment': 'Handover',
  'Project_Plan': 'Spreadsheet',
  'Deployment_Guide': 'Spreadsheet',
  'Commercial': 'Spreadsheet',
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const getIcon = (filename) => {
  for (const [key, Icon] of Object.entries(iconMap)) {
    if (filename.includes(key)) return Icon;
  }
  return FileText;
};

const getCategory = (filename) => {
  for (const [key, cat] of Object.entries(categoryMap)) {
    if (filename.includes(key)) return cat;
  }
  return 'Other';
};

export const DocumentsPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await api.get('/downloads');
      setFiles(response.data.data.files || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    setDownloading(file.filename);
    try {
      const response = await api.get(file.url, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${file.filename}`);
    } catch (error) {
      toast.error(`Failed to download ${file.filename}`);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="docs-loading">
        <div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  const grouped = files.reduce((acc, f) => {
    const cat = getCategory(f.filename);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shadow-blue-500/20">
          <FolderOpen className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="docs-title">Project Documents</h1>
          <p className="text-sm text-slate-500">Download project documentation, handover docs, and spreadsheets</p>
        </div>
      </div>

      {files.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No documents available</p>
        </div>
      ) : (
        Object.entries(grouped).map(([category, catFiles]) => (
          <div key={category}>
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">{category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {catFiles.map((file) => {
                const Icon = getIcon(file.filename);
                const gradient = colorMap[file.type] || 'from-slate-400 to-slate-500';
                const isDownloading = downloading === file.filename;
                const displayName = file.filename.replace('Ariskool_', '').replace('Handover_', 'Handover: ').replace(/_/g, ' ').replace(/\.\w+$/, '');

                return (
                  <button
                    key={file.filename}
                    onClick={() => handleDownload(file)}
                    disabled={isDownloading}
                    className="glass-card p-4 text-left hover:shadow-lg transition-all duration-200 group disabled:opacity-60"
                    data-testid={`doc-${file.filename}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-slate-800 truncate group-hover:text-teal-600 transition-colors">{displayName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${file.type === 'docx' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {file.type}
                          </span>
                          <span className="text-xs text-slate-400">{formatSize(file.size)}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {isDownloading ? (
                          <div className="h-5 w-5 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
                        ) : (
                          <Download className="h-5 w-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default DocumentsPage;
